import { ColumnDef } from "@tanstack/react-table";
import { IconButton, Tooltip } from "@nike/eds";
import { AlertCircle, Download, Show } from "@nike/nike-design-system-icons";
import { FilingPackEvent } from "../../shared/Types";
import { formatDateTime, isPreviewAvailable } from "../../shared/utils";

export type EventsTableColumn<T> = ColumnDef<T, any> & {
    header: string | React.ReactNode | (() => React.ReactNode);
    accessorKey: keyof T | string;
    enableHiding?: boolean;
    enableSorting?: boolean;
};

export const getEventsTableColumns = (
    onDownload?: (s3Reference: string) => void,
    onShowPayload?: (s3Reference: string) => void,
): EventsTableColumn<FilingPackEvent>[] => [
    {
        header: () => (<span title="Event Timestamp">Event Timestamp</span>),
        accessorKey: "event_timestamp",
        cell: ({ row }) => formatDateTime(row.original.event_timestamp, { includeSeconds: true }),
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Event Type">Event Type</span>),
        accessorKey: "event_type",
        size: 150,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Status">Status</span>),
        accessorKey: "status",
        size: 120,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Payload">Payload</span>),
        accessorKey: "s3_reference",
        cell: ({ row }) => (
            <div>
                <IconButton
                    icon={<Download />}
                    label="Download payload"
                    size="small"
                    variant="ghost"
                    disabled={!row.original.s3_reference}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (row.original.s3_reference && onDownload) {
                            onDownload(row.original.s3_reference);
                        }
                    }}
                />
                &nbsp;
                {!row.original.s3_reference || !isPreviewAvailable(row.original.payload_size) ? (
                    <Tooltip bodySlot="Payload too large for viewing. Use download instead.">
                        <IconButton
                            icon={<Show />}
                            label="View payload"
                            size="small"
                            variant="ghost"
                            disabled
                        />
                    </Tooltip>
                ) : (
                    <IconButton
                        icon={<Show />}
                        label="View payload"
                        size="small"
                        variant="ghost"
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            if (row.original.s3_reference && onShowPayload) {
                                onShowPayload(row.original.s3_reference);
                            }
                        }}
                    />
                )}
            </div>
        ),
        size: 100,
        enableSorting: false,
        enableColumnFilter: false,
    },
    {
        header: () => (<span title="Error">Error</span>),
        accessorKey: "error_message",
        cell: ({row}) => {
            return row.original.error_message != null ?
                <Tooltip bodySlot={row.original.error_message}>
                    <AlertCircle />
                </Tooltip> : null;
        },
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
    }
];
